<template>
  <div class="polygon-property">
    <n-collapse :default-expanded-names="defaultExpanded" class="pm-collapse">
      <n-collapse-item title="选项" name="options">
        <n-checkbox
          :checked="constructionChecked"
          :disabled="!canEdit"
          @update:checked="handleForConstruction"
        >
          作为构造线
        </n-checkbox>
      </n-collapse-item>

      <n-collapse-item title="参数" name="parameters">
        <div class="param-grid">
          <div class="param-row param-row-sides">
            <n-text depth="3" class="param-label">边数</n-text>
            <n-input-number
              :value="sidesValue"
              size="small"
              :disabled="!canEditSides"
              :show-button="true"
              :precision="0"
              :min="3"
              @update:value="handleSidesInput"
            />
          </div>

          <div class="param-row param-row-mode">
            <n-radio-group
              :value="modeValue"
              :disabled="!canEditMode"
              @update:value="handleModeChange"
            >
              <n-radio value="inscribed">内切圆</n-radio>
              <n-radio value="circumscribed">外接圆</n-radio>
            </n-radio-group>
          </div>

          <div class="param-row">
            <n-text depth="3" class="param-label">X</n-text>
            <n-input-number
              :value="center2d?.[0] ?? null"
              size="small"
              :disabled="!canEditParameters"
              :show-button="true"
              :precision="8"
              :step="1"
              @update:value="(value) => handleCenterInput('x', value)"
            />
          </div>

          <div class="param-row">
            <n-text depth="3" class="param-label">Y</n-text>
            <n-input-number
              :value="center2d?.[1] ?? null"
              size="small"
              :disabled="!canEditParameters"
              :show-button="true"
              :precision="8"
              :step="1"
              @update:value="(value) => handleCenterInput('y', value)"
            />
          </div>

          <div class="param-row">
            <n-text depth="3" class="param-label">R</n-text>
            <n-input-number
              :value="radius ?? null"
              size="small"
              :disabled="!canEditParameters"
              :show-button="true"
              :precision="8"
              :step="1"
              @update:value="handleRadiusInput"
            />
          </div>

          <div class="param-row">
            <n-text depth="3" class="param-label">角</n-text>
            <n-input-number
              :value="rotation ?? null"
              size="small"
              :disabled="!canEditParameters"
              :show-button="true"
              :precision="8"
              :step="1"
              @update:value="handleRotationInput"
            />
          </div>
        </div>
      </n-collapse-item>
    </n-collapse>
  </div>
</template>

<script setup lang="ts">
import * as THREE from 'three';
import { computed, onBeforeUnmount, ref } from 'vue';
import {
  NCheckbox,
  NCollapse,
  NCollapseItem,
  NInputNumber,
  NRadio,
  NRadioGroup,
  NText,
} from 'naive-ui';
import {
  isSketchCollectionObject,
  type PolygonOptions,
  type PolygonParameters,
  //------ 统一单位转换 开始------
  convertMmToM,
  convertMToMm,
  roundToEightDecimalPlaces,
  //------ 统一单位转换 结束------
} from '@puppycad/cad-core';
import { PolygonMode } from '@/core';
import { useGetSelectLine, useGetSelectPolygon, useViewer } from '@/hooks';

defineOptions({
  inheritAttrs: false,
});

/**
 * 默认多边形选项。
 */
//------ 多边形默认内切圆 开始------
const defaultOptions: PolygonOptions = {
  sides: 6,
  mode: PolygonMode.Inscribed,
  forConstruction: false,
};
//------ 多边形默认内切圆 结束------

const { getViewer } = useViewer();
const { onPolygonParametersChanged, onPolygonDrawingInProgressChanged } = useGetSelectPolygon();
const { getSelectedLine, onLineChanged } = useGetSelectLine();

const localPolygonOptions = ref<PolygonOptions>({ ...defaultOptions });
const localPolygonParameters = ref<PolygonParameters | null>(null);
const constructionCheckedValue = ref(false);
const polygonDrawingInProgressValue = ref(false);

localPolygonOptions.value = getViewer()?.getPolygonDrawingOptions?.() ?? { ...defaultOptions };
localPolygonParameters.value = getViewer()?.getPolygonDrawingParameters?.() ?? null;

const polygonParametersSub = onPolygonParametersChanged().subscribe((params) => {
  if (params) {
    localPolygonParameters.value = params;
  }
});

const polygonDrawingInProgressSub = onPolygonDrawingInProgressChanged().subscribe((inProgress) => {
  polygonDrawingInProgressValue.value = inProgress;
});

const polygonLineChangedSub = onLineChanged().subscribe(() => {
  syncConstructionCheckedState();
});

onBeforeUnmount(() => {
  polygonParametersSub.unsubscribe();
  polygonDrawingInProgressSub.unsubscribe();
  polygonLineChangedSub.unsubscribe();
});

const isPolygonDrawingInProgress = computed(() => polygonDrawingInProgressValue.value);
const canEdit = computed(() => Boolean(getViewer()) && !isPolygonDrawingInProgress.value);
const canEditParameters = computed(() => Boolean(getViewer() && localPolygonParameters.value) && !isPolygonDrawingInProgress.value);
const options = computed(() => localPolygonOptions.value);
const params = computed(() => localPolygonParameters.value);
//------ 统一单位转换 开始------
const center2d = computed(() => {
  const c = params.value?.center2d ?? null;
  if (!c) return null;
  return [
    roundToEightDecimalPlaces(convertMToMm(c[0])),
    roundToEightDecimalPlaces(convertMToMm(c[1])),
  ];
});
const radius = computed(() => {
  const r = params.value?.radius ?? null;
  if (r === null) return null;
  return roundToEightDecimalPlaces(convertMToMm(r));
});
//------ 统一单位转换 结束------
const rotation = computed(() => params.value?.rotation ?? null);
const sidesValue = computed(() => options.value.sides ?? null);
const modeValue = computed(() => options.value.mode);
const constructionChecked = computed(() => constructionCheckedValue.value);
const canEditSides = computed(() => Boolean(getViewer()) && !isPolygonDrawingInProgress.value);
const canEditMode = computed(() => canEditSides.value);

const defaultExpanded = ['options', 'parameters'];

/**
 * 解析当前选中的多边形集合对象。
 * @returns 多边形集合对象或 null。
 */
function resolveSelectedPolygonCollection() {
  const selectedLine = getSelectedLine();
  if (!selectedLine?.closedCollection) {
    return null;
  }
  if (!isSketchCollectionObject(selectedLine.closedCollection)) {
    return null;
  }
  return selectedLine.closedCollection;
}

/**
 * 解析当前可编辑的多边形集合对象。
 * @returns 多边形集合对象或 null。
 */
function resolveEditablePolygonCollection() {
  return getViewer()?.getCurrentPolygonDrawingCollectionObject?.() ?? resolveSelectedPolygonCollection();
}

/**
 * 同步“作为构造线”勾选状态。
 */
function syncConstructionCheckedState(): void {
  const polygonCollection = resolveEditablePolygonCollection();
  if (polygonCollection) {
    const firstEdge = Array.from(polygonCollection.collectSet)[0];
    constructionCheckedValue.value = Boolean(firstEdge?.isConstruction);
    return;
  }
  constructionCheckedValue.value = Boolean(localPolygonOptions.value.forConstruction);
}

/**
 * 更新多边形选项并立即刷新预览。
 * @param patch 选项补丁。
 */
function updateOptions(patch: Partial<PolygonOptions>): void {
  const viewer = getViewer();
  const currentOptions = viewer?.getPolygonDrawingOptions?.();
  if (!viewer || !currentOptions) {
    return;
  }

  viewer.setPolygonDrawingOptions?.({ ...currentOptions, ...patch });
  localPolygonOptions.value = { ...localPolygonOptions.value, ...patch };
  viewer.refreshPolygonDrawingPreview?.();
}

/**
 * 处理构造线开关。
 * @param value 是否作为构造线。
 */
function handleForConstruction(value: boolean): void {
  if (isPolygonDrawingInProgress.value) {
    return;
  }

  const polygonCollection = resolveEditablePolygonCollection();
  if (polygonCollection) {
    const viewer = getViewer();
    polygonCollection.collectSet.forEach((edge) => {
      edge.isConstruction = value;
      viewer?.interactionStateManager?.syncObjectBaseVisualState(edge);
    });
    if (value || !viewer?.shadedSketchContoursEnabled) {
      polygonCollection.clearContourDisplay?.();
    } else {
      polygonCollection.syncContour?.();
    }
  }

  updateOptions({ forConstruction: value });
  constructionCheckedValue.value = value;
}

/**
 * 处理边数输入。
 * @param value 边数。
 */
function handleSidesInput(value: number | null): void {
  if (isPolygonDrawingInProgress.value || value === null || Number.isNaN(value)) {
    return;
  }

  const normalizedSides = Math.max(3, Math.floor(value));
  updateOptions({ sides: normalizedSides });

  const currentParameters = localPolygonParameters.value;
  const viewer = getViewer();
  if (!currentParameters || !viewer) {
    return;
  }

  if (viewer.syncPolygonDrawingPreviewByParameters?.(currentParameters)) {
    localPolygonParameters.value = viewer.getPolygonDrawingParameters?.() ?? currentParameters;
    syncConstructionCheckedState();
    return;
  }

  const polygonCollection = resolveEditablePolygonCollection();
  if (!polygonCollection) {
    return;
  }

  viewer.rebuildPolygonDrawingCollectionByParameters?.(currentParameters, polygonCollection);
  localPolygonParameters.value = viewer.getPolygonDrawingParameters?.() ?? currentParameters;
  syncConstructionCheckedState();
}

/**
 * 处理模式切换。
 * @param value 模式。
 */
function handleModeChange(value: PolygonMode): void {
  if (isPolygonDrawingInProgress.value) {
    return;
  }

  updateOptions({ mode: value });
  const currentParameters = localPolygonParameters.value;
  if (!currentParameters) {
    return;
  }

  if (!syncDrawingPreviewByParameters(currentParameters)) {
    rebuildEditablePolygonByParameters(currentParameters);
  }
}

/**
 * 处理中心点输入。
 * @param axis 坐标轴。
 * @param value 输入值。
 */
function handleCenterInput(axis: 'x' | 'y', value: number | null): void {
  if (isPolygonDrawingInProgress.value || value === null || Number.isNaN(value)) {
    return;
  }

  const currentParameters = localPolygonParameters.value;
  if (!currentParameters) {
    return;
  }

  const nextParameters = clonePolygonParameters(currentParameters);
  //------ 统一单位转换 开始------
  // 将用户输入的毫米转换为底层所需的米
  if (axis === 'x') {
    nextParameters.center2d[0] = convertMmToM(value);
  } else {
    nextParameters.center2d[1] = convertMmToM(value);
  }
  //------ 统一单位转换 结束------
  syncWorldCenterFromLocalCenter2d(nextParameters);

  if (!syncDrawingPreviewByParameters(nextParameters)) {
    rebuildEditablePolygonByParameters(nextParameters);
  }
}

/**
 * 处理半径输入。
 * @param value 半径值。
 */
function handleRadiusInput(value: number | null): void {
  if (isPolygonDrawingInProgress.value || value === null || Number.isNaN(value) || value <= 1e-8) {
    return;
  }

  const currentParameters = localPolygonParameters.value;
  if (!currentParameters) {
    return;
  }

  const nextParameters = clonePolygonParameters(currentParameters);
  //------ 统一单位转换 开始------
  // 将用户输入的毫米转换为底层所需的米
  nextParameters.radius = convertMmToM(value);
  //------ 统一单位转换 结束------

  if (!syncDrawingPreviewByParameters(nextParameters)) {
    rebuildEditablePolygonByParameters(nextParameters);
  }
}

/**
 * 处理旋转角输入。
 * @param value 旋转角。
 */
function handleRotationInput(value: number | null): void {
  if (isPolygonDrawingInProgress.value || value === null || Number.isNaN(value)) {
    return;
  }

  const currentParameters = localPolygonParameters.value;
  if (!currentParameters) {
    return;
  }

  const nextParameters = clonePolygonParameters(currentParameters);
  nextParameters.rotation = normalizeAngleDegrees(value);

  if (!syncDrawingPreviewByParameters(nextParameters)) {
    rebuildEditablePolygonByParameters(nextParameters);
  }
}

/**
 * 克隆多边形参数。
 * @param parameters 原始参数。
 * @returns 克隆结果。
 */
function clonePolygonParameters(parameters: PolygonParameters): PolygonParameters {
  return {
    center: [...parameters.center] as [number, number, number],
    center2d: [...parameters.center2d] as [number, number],
    radius: parameters.radius,
    rotation: parameters.rotation,
    sides: parameters.sides,
    mode: parameters.mode,
  };
}

/**
 * 将二维中心同步回世界坐标中心。
 * @param parameters 多边形参数。
 */
function syncWorldCenterFromLocalCenter2d(parameters: PolygonParameters): void {
  const viewer = getViewer();
  const currentReferencePlane = viewer?.currentReferencePlane;
  if (!viewer || !currentReferencePlane) {
    throw new Error('当前参考平面不可用，无法同步多边形中心。');
  }

  const centerWorld = currentReferencePlane.localToWorld(
    new THREE.Vector2(parameters.center2d[0], parameters.center2d[1]),
  );
  parameters.center = centerWorld.toArray() as [number, number, number];
}

/**
 * 归一化角度。
 * @param value 原始角度。
 * @returns 归一化角度。
 */
function normalizeAngleDegrees(value: number): number {
  let normalized = value % 360;
  if (normalized < 0) {
    normalized += 360;
  }
  return normalized;
}

/**
 * 尝试同步绘制预览。
 * @param parameters 多边形参数。
 * @returns 是否同步成功。
 */
function syncDrawingPreviewByParameters(parameters: PolygonParameters): boolean {
  const viewer = getViewer();
  if (!viewer) {
    return false;
  }

  const synced = viewer.syncPolygonDrawingPreviewByParameters?.(parameters) ?? false;
  if (synced) {
    localPolygonParameters.value = viewer.getPolygonDrawingParameters?.() ?? clonePolygonParameters(parameters);
  }
  return synced;
}

/**
 * 按参数重建当前可编辑多边形。
 * @param parameters 多边形参数。
 */
function rebuildEditablePolygonByParameters(parameters: PolygonParameters): void {
  const viewer = getViewer();
  const polygonCollection = resolveEditablePolygonCollection();
  if (!viewer || !polygonCollection) {
    return;
  }

  viewer.rebuildPolygonDrawingCollectionByParameters?.(parameters, polygonCollection);
  localPolygonParameters.value = viewer.getPolygonDrawingParameters?.() ?? clonePolygonParameters(parameters);
  syncConstructionCheckedState();
}

syncConstructionCheckedState();
</script>

<style scoped>
.polygon-property {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.pm-collapse :deep(.n-collapse-item__header) {
  padding: 6px 0;
}

.param-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.param-row {
  display: grid;
  grid-template-columns: 36px 1fr;
  gap: 8px;
  align-items: center;
}

.param-row-mode {
  grid-template-columns: 1fr;
}

.param-label {
  font-size: 12px;
}

.new-polygon-button {
  margin-top: 10px;
  width: 100%;
}
</style>
